/**
 * Generic PDF reader component that renders all pages vertically.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { pdfjs, Document, Page, PDFPageProxy } from 'react-pdf';
import { Button, InlineLoading } from 'carbon-components-react';
import { isIOS } from 'react-device-detect';
import { Virtuoso } from 'react-virtuoso';
import { usePDFPasswordHandler } from 'customHooks/usePDFPasswordHandler';

// Use the same worker configuration
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.js`;

export interface PDFReaderProps {
  fileUrl: string;
  className?: string;
  style?: React.CSSProperties;
  viewerContentHeight?: string | number;
  containerClassName?: string;
  showPageNumbers?: boolean;
  onDocumentLoadSuccess?: (numPages: number) => void;
  onDocumentLoadError?: (error: any) => void;
  onPageLoadSuccess?: (page: PDFPageProxy, pageNumber: number) => void;
}

const PDFReader: React.FC<PDFReaderProps> = ({
  fileUrl,
  className = '',
  style = {},
  viewerContentHeight,
  containerClassName = 'pdf-reader-container',
  showPageNumbers = true,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  onPageLoadSuccess
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Memoize callbacks to prevent infinite re-renders
  const handleSuccessCallback = useCallback(() => {
    setIsLoading(false);
    onDocumentLoadSuccess?.(numPages);
  }, [numPages, onDocumentLoadSuccess]);

  const handleErrorCallback = useCallback(
    (error: any) => {
      setIsLoading(false);
      onDocumentLoadError?.(error);
    },
    [onDocumentLoadError]
  );

  const {
    state: passwordState,
    actions: passwordActions
  } = usePDFPasswordHandler(handleSuccessCallback, handleErrorCallback);

  // PDF Page Sizing Strategy:
  // Use width-based sizing only (no scale prop) to avoid conflicts.
  // Calculate available width = container width - padding, then let react-pdf
  // automatically scale the page to fit this width while maintaining aspect ratio.
  // Reset states when fileUrl changes
  useEffect(() => {
    setIsLoading(true);
    passwordActions.resetStatesOnly();
  }, [fileUrl, passwordActions]);

  useEffect(() => {
    const container = document.querySelector(`.${containerClassName}`);
    if (container) {
      const width = container.clientWidth;
      const padding = 32; // 16px padding on each side
      const availableWidth = width - padding;
      setPageWidth(availableWidth);
    } else {
      // Fallback calculation using window width if container not found yet
      const padding = 32; // 16px padding on each side
      const fallbackWidth = window.innerWidth - padding;
      setPageWidth(fallbackWidth);
    }
  }, [containerClassName]);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
    passwordActions.handleDocumentLoadSuccess();
    onDocumentLoadSuccess?.(numPages);
  };

  const handleDocumentLoadError = (error: any) => {
    setIsLoading(false);
    passwordActions.handleDocumentLoadError(error);
    onDocumentLoadError?.(error);
  };

  const handlePageLoadSuccess = (page: PDFPageProxy, pageNumber: number) => {
    onPageLoadSuccess?.(page, pageNumber);

    // Handle iOS-specific area limitations
    if (isIOS && pageWidth) {
      const { originalHeight, originalWidth } = page;
      const maxAreaLimit = 16777216;
      const pixelRatio = window.devicePixelRatio || 1;
      const scale = pageWidth / originalWidth;

      const areaAfterZoom =
        originalHeight *
        pixelRatio *
        scale *
        (originalWidth * pixelRatio * scale);

      // If the area would exceed limits, reduce the width proportionally
      if (areaAfterZoom > maxAreaLimit) {
        const ratio = originalWidth / originalHeight;
        const adjustedWidth =
          Math.sqrt(maxAreaLimit / ratio) / (pixelRatio * scale);
        setPageWidth(Math.min(pageWidth, Number(adjustedWidth.toFixed(2))));
      }
    }
  };

  const LoadingComponent = () => (
    <div className="pdf-loading-message">
      <InlineLoading description="Loading PDF..." />
    </div>
  );

  const PageLoadingPlaceholder = () => (
    <div className="pdf-page-loading-placeholder">
      <InlineLoading description="Loading page..." />
    </div>
  );

  const handleRetryPassword = () => {
    setNumPages(0);
    passwordActions.handleRetryPassword();
  };

  const ErrorComponent = () => (
    <div className="pdf-loading-message pdf-error-container">
      <div className="pdf-error-message">
        {passwordState.passwordCancelled
          ? 'Password required to view this PDF document'
          : 'Failed to load PDF document'}
      </div>
      {passwordState.passwordCancelled && (
        <div className="pdf-error-button-container">
          <Button onClick={handleRetryPassword} kind="secondary">
            Enter password
          </Button>
        </div>
      )}
    </div>
  );

  const renderPage = (index: number) => {
    const pageNumber = index + 1;
    return (
      <div key={`page-${pageNumber}`} className="pdf-page-container">
        <Page
          pageNumber={pageNumber}
          width={pageWidth}
          onLoadSuccess={(page) => handlePageLoadSuccess(page, pageNumber)}
          loading={<PageLoadingPlaceholder />}
          renderAnnotationLayer={false}
        />
        {showPageNumbers && (
          <div className="pdf-page-number">
            Page {pageNumber}/{numPages}
          </div>
        )}
      </div>
    );
  };

  if (passwordState.hasError) {
    return <ErrorComponent />;
  }

  if (!fileUrl) {
    return (
      <div className="pdf-loading-message">
        <div style={{ textAlign: 'center', color: '#6f6f6f' }}>
          No PDF file URL provided
        </div>
      </div>
    );
  }

  return (
    <div className={`pdf-reader-wrapper ${className}`} style={style}>
      <div className={containerClassName}>
        <Document
          key={passwordState.documentKey}
          file={fileUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          onPassword={passwordActions.handlePassword}
          loading={<LoadingComponent />}
          options={{
            disableAnnotationLayer: true
          }}
        >
          <div
            className="pdf-content-scrollable"
            style={{
              height: viewerContentHeight || '100%', // Changed maxHeight to height
              width: '100%'
            }}
          >
            {!isLoading && numPages > 0 && (
              <Virtuoso
                totalCount={numPages}
                itemContent={renderPage}
                style={{ height: '100%' }}
              />
            )}
          </div>
        </Document>
      </div>
    </div>
  );
};

PDFReader.displayName = 'PDFReader';

export default PDFReader;
